import { makeAutoObservable, runInAction } from 'mobx';
import { rootContainer, TYPE } from '../../services/container/root';
import {
    ACTIONS_VIEW,
    ICustomizationClassic,
    POLICY_RULE_DISPLAY,
    PORTAL_BACKGROUND_SIZE,
    PORTAL_BACKGROUND_TYPE,
    PORTAL_POSITION,
    S2E_LAYOUT_TYPE,
    TRACKING_ERROR_MESSAGE_STYLE,
    VARIANTS_VIEW
} from '../../models/User';
import CustomizationsService from '../../services/api/customizations-service';
import {IChangeable, ISaveable, WithOld} from '../types';
import { INewBackgroundImageData, S2ELayoutItemStore } from './s2e-layout-item-store';

interface ICustomizationClassicChanges extends Partial<ICustomizationClassic> {
    _return_portal_new_background_image?: INewBackgroundImageData;
    _return_portal_delete_background_image?: boolean;
    _tracking_portal_new_background_image?: INewBackgroundImageData;
    _tracking_portal_delete_background_image?: boolean;
    _return_portal_new_cover_image?: INewBackgroundImageData;
    _return_portal_delete_cover_image?: boolean;
}


export class CustomizationClassicStore implements ISaveable, IChangeable<ICustomizationClassic> {
    private _changes: ICustomizationClassicChanges = {};
    private _model: ICustomizationClassic = {} as ICustomizationClassic;
    private _isSaving: boolean = false;

    private _return_portal_background_image_dimensions?: string;
    private _tracking_portal_background_image_dimensions?: string;
    private _return_portal_cover_image_dimensions?: string;

    private _s2e_layout_data: S2ELayoutItemStore[] = [];

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private get service(): CustomizationsService {
        return rootContainer.get<CustomizationsService>(TYPE.CustomizationsService);
    }

    public isSaving(): boolean {
        return this._isSaving;
    }

    private setModel(model: ICustomizationClassic): void {
        this._model = ensureValidModelCopy(model);

        this._s2e_layout_data = [];

        Array.from({ length: 4 }, (_, i) => i + 1).forEach((_, i) => {
            this._s2e_layout_data.push(
              new S2ELayoutItemStore(this._model.s2e_layout_data?.[i])
            )
        });

        this.discardChanges();
        if (this.returnPortalBackgroundType === PORTAL_BACKGROUND_TYPE.IMAGE && this.returnPortalBackgroundValue) {
            this._setReturnPortalBackgroundImageOriginalDimensions();
        }
        if (this.trackingPortalBackgroundType === PORTAL_BACKGROUND_TYPE.IMAGE && this.trackingPortalBackgroundValue) {
            this._setTrackingPortalBackgroundImageOriginalDimensions();
        }
    }

    public get font(): string|undefined { return this._changes.font ?? this._model.font; }
    public get inputTextColor(): string|undefined { return this._changes.input_text_color ?? this._model.input_text_color; }
    public get inputBackgroundColor(): string|undefined { return this._changes.input_background_color ?? this._model.input_background_color; }
    public get buttonBackgroundColor(): string|undefined { return this._changes.button_background_color ?? this._model.button_background_color; }
    public get buttonTextColor(): string|undefined { return this._changes.button_text_color ?? this._model.button_text_color; }
    public get textColor(): string|undefined { return this._changes.text_color ?? this._model.text_color; }
    public get titleColor(): string|undefined { return this._changes.title_color ?? this._model.title_color; }
    public get errorColor(): string|undefined { return this._changes.error_color ?? this._model.error_color; }
    public get minEmbeddedOffsetTop(): number { return this._changes.min_embedded_offset_top ?? this._model.min_embedded_offset_top; }
    public get skuDisplay(): boolean { return this._changes.sku_display ?? this._model.sku_display; }
    public get variantSelection(): VARIANTS_VIEW { return this._changes.variant_selection ?? this._model.variant_selection; }
    public get actionSelection(): ACTIONS_VIEW { return this._changes.action_selection ?? this._model.action_selection; }
    public get displayVertical(): boolean { return this._changes.display_vertical ?? this._model.display_vertical ?? false; }
    public get showActionsDescriptions(): boolean { return this._changes.show_actions_descriptions ?? this._model.show_actions_descriptions ?? false; }
    public get showNewItemsFirst(): boolean { return this._changes.show_new_items_first ?? this._model.show_new_items_first ?? false; }
    public get orderTrackingShowETA(): boolean { return this._changes.order_tracking_show_eta ?? this._model.order_tracking_show_eta ?? false; }
    public get trackingErrorMessageStyle(): TRACKING_ERROR_MESSAGE_STYLE {return this._changes.tracking_error_message_style ?? this._model.tracking_error_message_style; }
    public get portalCss(): string {return this._changes.portal_css ?? this._model.portal_css; }
    public get portalPosition(): PORTAL_POSITION {return this._changes.portal_position ?? this._model.portal_position; }
    public get topMargin(): number { return this._changes.top_margin ?? this._model.top_margin; }
    public get bottomMargin(): number { return this._changes.bottom_margin ?? this._model.bottom_margin; }
    public get containerMargins(): number { return this._changes.container_margins ?? this._model.container_margins; }
    public get portalBorderRadius(): number { return this._changes.portal_border_radius ?? this._model.portal_border_radius; }
    public get buttonBorderRadius(): number { return this._changes.button_border_radius ?? this._model.button_border_radius; }
    public get elementsBorderRadius(): number { return this._changes.elements_border_radius ?? this._model.elements_border_radius; }
    public get returnPortalBackgroundType(): PORTAL_BACKGROUND_TYPE { return this._changes.return_portal_background_type ?? this._model.return_portal_background_type; }
    public get returnPortalBackgroundValue(): string | undefined {
        if (this.returnPortalBackgroundType === PORTAL_BACKGROUND_TYPE.IMAGE) {
            if (this._changes._return_portal_delete_background_image) {
                return undefined;
            }
            if (this._changes._return_portal_new_background_image) {
                return this._getNewBackgroundImageBase64Str(this._changes._return_portal_new_background_image)
            } else if (this._model.return_portal_background_type === PORTAL_BACKGROUND_TYPE.IMAGE) {
                return this._model.return_portal_background_value;
            }
        } else {
            if (this.returnPortalBackgroundType === this._model.return_portal_background_type) {
                return this._changes.return_portal_background_value ?? this._model.return_portal_background_value;
            } else {
                return this._changes.return_portal_background_value;
            }
        }
        return undefined;
    }
    public get returnPortalBackgroundImageDimensions() {
        return this._return_portal_background_image_dimensions;
    }
    public get returnPortalBackgroundSize(): PORTAL_BACKGROUND_SIZE { return this._changes.return_portal_background_size ?? this._model.return_portal_background_size; }
    public get returnPortalForegroundColor(): string { return this._changes.return_portal_foreground_color ?? this._model.return_portal_foreground_color; }
    public get returnPortalForegroundOpacity(): number { return this._changes.return_portal_foreground_opacity ?? this._model.return_portal_foreground_opacity; }
    public get trackingPortalBackgroundType(): PORTAL_BACKGROUND_TYPE { return this._changes.tracking_portal_background_type ?? this._model.tracking_portal_background_type; }
    public get trackingPortalBackgroundValue(): string | undefined {
        if (this.trackingPortalBackgroundType === PORTAL_BACKGROUND_TYPE.IMAGE) {
            if (this._changes._tracking_portal_delete_background_image) {
                return undefined;
            }
            if (this._changes._tracking_portal_new_background_image) {
                return this._getNewBackgroundImageBase64Str(this._changes._tracking_portal_new_background_image)
            } else if (this._model.tracking_portal_background_type === PORTAL_BACKGROUND_TYPE.IMAGE) {
                return this._model.tracking_portal_background_value;
            }
        } else {
            if (this.trackingPortalBackgroundType === this._model.tracking_portal_background_type) {
                return this._changes.tracking_portal_background_value ?? this._model.tracking_portal_background_value;
            } else {
                return this._changes.tracking_portal_background_value;
            }
        }
        return undefined;
    }
    public get trackingPortalBackgroundImageDimensions() {
        return this._tracking_portal_background_image_dimensions;
    }
    public get trackingPortalBackgroundSize(): PORTAL_BACKGROUND_SIZE { return this._changes.tracking_portal_background_size ?? this._model.tracking_portal_background_size; }
    public get trackingPortalForegroundColor(): string { return this._changes.tracking_portal_foreground_color ?? this._model.tracking_portal_foreground_color; }
    public get trackingPortalForegroundOpacity(): number { return this._changes.tracking_portal_foreground_opacity ?? this._model.tracking_portal_foreground_opacity; }
    public get policyRuleDisplay(): POLICY_RULE_DISPLAY { return this._changes.policy_rule_display ?? this._model.policy_rule_display; }
    public get footerBackgroundColor(): string { return this._changes.footer_background_color ?? this._model.footer_background_color; }
    public get footerBackgroundOpacity(): number { return this._changes.footer_background_opacity ?? this._model.footer_background_opacity; }
    public get switchToExchangeLayoutType(): S2E_LAYOUT_TYPE { return this._changes.s2e_layout_type ?? this._model.s2e_layout_type ?? S2E_LAYOUT_TYPE.ONE_RECTANGLE; }
    public get switchToExchangeLayoutData(): S2ELayoutItemStore[] { return this._s2e_layout_data; }
    public get switchToExchangeLayoutImages(): (string|undefined)[] { return this._s2e_layout_data.map((item) => item.image); }
    public get returnPortalCoverImage(): string | undefined {
        if (this._changes._return_portal_delete_cover_image) {
            return undefined;
        }
        if (this._changes._return_portal_new_cover_image) {
            return this._getNewBackgroundImageBase64Str(this._changes._return_portal_new_cover_image)
        }
        return this._changes.return_portal_cover_image ?? this._model.return_portal_cover_image;
    }
    public get returnPortalCoverImageDimensions() { return this._return_portal_cover_image_dimensions; }

    public setFont(v: string) {
        this._changes.font = v;
        this._clearUnneededChanges();
    }

    public setInputTextColor(v: string) {
        this._changes.input_text_color = v;
        this._clearUnneededChanges();
    }

    public setInputBackgroundColor(v: string) {
        this._changes.input_background_color = v;
        this._clearUnneededChanges();
    }

    public setButtonBackgroundColor(v: string) {
        this._changes.button_background_color = v;
        this._clearUnneededChanges();
    }

    public setButtonTextColor(v: string) {
        this._changes.button_text_color = v;
        this._clearUnneededChanges();
    }

    public setTextColor(v: string) {
        this._changes.text_color = v;
        this._clearUnneededChanges();
    }

    public setTitleColor(v: string) {
        this._changes.title_color = v;
        this._clearUnneededChanges();
    }

    public setErrorColor(v: string) {
        this._changes.error_color = v;
        this._clearUnneededChanges();
    }

    public setMinEmbeddedOffsetTop(v: number) {
        this._changes.min_embedded_offset_top = v;
        this._clearUnneededChanges();
    }

    public setSkuDisplay(v: boolean) {
        this._changes.sku_display = v;
        this._clearUnneededChanges();
    }

    public setVariantSelection(v: VARIANTS_VIEW) {
        this._changes.variant_selection = v;
        this._clearUnneededChanges();
    }

    public setActionSelection(v: ACTIONS_VIEW) {
        this._changes.action_selection = v;
        this._clearUnneededChanges();
    }

    public setDisplaVertical(v: boolean) {
        this._changes.display_vertical = v;
        this._clearUnneededChanges();
    }

    public setShowActionsDescriptions(v: boolean) {
        this._changes.show_actions_descriptions = v;
        this._clearUnneededChanges();
    }

    public setShowNewItemsFirst(v: boolean) {
        this._changes.show_new_items_first = v;
        this._clearUnneededChanges();
    }

    public setOrderTrackingShowETA(v: boolean) {
        this._changes.order_tracking_show_eta = v;
        this._clearUnneededChanges();
    }

    public setTrackingErrorMessageStyle(v: TRACKING_ERROR_MESSAGE_STYLE) {
        this._changes.tracking_error_message_style = v;
        this._clearUnneededChanges();
    }

    public setPortalCss(v: string) {
        this._changes.portal_css = v;
        this._clearUnneededChanges();
    }

    public setPortalPosition(v: PORTAL_POSITION) {
        this._changes.portal_position = v;
        this._clearUnneededChanges();
    }

    public setTopMargin(v: number) {
        this._changes.top_margin = v;
        this._clearUnneededChanges();
    }

    public setBottomMargin(v: number) {
        this._changes.bottom_margin = v;
        this._clearUnneededChanges();
    }

    public setContainerMargins(v: number) {
        this._changes.container_margins = v;
        this._clearUnneededChanges();
    }

    public setPortalBorderRadius(v: number) {
        this._changes.portal_border_radius = v;
        this._clearUnneededChanges();
    }

    public setButtonBorderRadius(v: number) {
        this._changes.button_border_radius = v;
        this._clearUnneededChanges();
    }

    public setElementsBorderRadius(v: number) {
        this._changes.elements_border_radius = v;
        this._clearUnneededChanges();
    }

    public setReturnPortalBackgroundType(v: PORTAL_BACKGROUND_TYPE) {
        this._changes.return_portal_background_type = v;
        if (this._changes.return_portal_background_type === this._model.return_portal_background_type) {
            this._changes.return_portal_background_value = this._model.return_portal_background_value;
        } else {
            if (v === PORTAL_BACKGROUND_TYPE.COLOR) {
                this._changes.return_portal_background_value = 'ffffff';
            } else {
                delete this._changes.return_portal_background_value;
            }
        }
        this._clearUnneededChanges();
    }

    public setReturnPortalBackgroundValue(v: string) {
        this._changes.return_portal_background_value = v;
        this._clearUnneededChanges();
    }

    public uploadReturnPortalBackgroundImage(type: string, base64_content: string) {
        this._changes._return_portal_new_background_image = {
            base64_content,
            type
        };
        delete this._changes._return_portal_delete_background_image;
        this._setReturnPortalBackgroundImageOriginalDimensions();
    }

    private _setReturnPortalBackgroundImageOriginalDimensions() {
        if (!this.returnPortalBackgroundValue) return;

        this._loadBackgroundImage(this.returnPortalBackgroundValue, (img: HTMLImageElement) => {
            this._return_portal_background_image_dimensions = img.width + 'x' + img.height;
        })
    }

    public deleteReturnPortalBackgroundImage() {
        this._changes._return_portal_delete_background_image = !!(this._model.return_portal_background_type === PORTAL_BACKGROUND_TYPE.IMAGE && this._model.return_portal_background_value);
        delete this._changes._return_portal_new_background_image;
    }

    public setReturnPortalBackgroundSize(v: PORTAL_BACKGROUND_SIZE) {
        this._changes.return_portal_background_size = v;
        this._clearUnneededChanges();
    }

    public setReturnPortalForegroundColor(v: string) {
        this._changes.return_portal_foreground_color = v;
        this._clearUnneededChanges();
    }

    public setReturnPortalForegroundOpacity(v: number) {
        this._changes.return_portal_foreground_opacity = v;
        this._clearUnneededChanges();
    }

    public setTrackingPortalBackgroundType(v: PORTAL_BACKGROUND_TYPE) {
        this._changes.tracking_portal_background_type = v;
        if (this._changes.tracking_portal_background_type === this._model.tracking_portal_background_type) {
            this._changes.tracking_portal_background_value = this._model.tracking_portal_background_value;
        } else {
            if (v === PORTAL_BACKGROUND_TYPE.COLOR) {
                this._changes.tracking_portal_background_value = 'ffffff';
            } else {
                delete this._changes.tracking_portal_background_value;
            }
        }
        this._clearUnneededChanges();
    }

    public setTrackingPortalBackgroundValue(v: string) {
        this._changes.tracking_portal_background_value = v;
        this._clearUnneededChanges();
    }

    public uploadTrackingPortalBackgroundImage(type: string, base64_content: string) {
        this._changes._tracking_portal_new_background_image = {
            base64_content,
            type
        };
        delete this._changes._tracking_portal_delete_background_image;
        this._setTrackingPortalBackgroundImageOriginalDimensions();
    }

    private _setTrackingPortalBackgroundImageOriginalDimensions() {
        if (!this.trackingPortalBackgroundValue) return;

        this._loadBackgroundImage(this.trackingPortalBackgroundValue, (img: HTMLImageElement) => {
            this._return_portal_background_image_dimensions = img.width + 'x' + img.height;
        })
    }

    public deleteTrackingPortalBackgroundImage() {
        this._changes._return_portal_delete_background_image = !!(this._model.tracking_portal_background_type === PORTAL_BACKGROUND_TYPE.IMAGE && this._model.tracking_portal_background_value);
        delete this._changes._tracking_portal_new_background_image;
    }

    public setTrackingPortalBackgroundSize(v: PORTAL_BACKGROUND_SIZE) {
        this._changes.tracking_portal_background_size = v;
        this._clearUnneededChanges();
    }

    public setTrackingPortalForegroundColor(v: string) {
        this._changes.tracking_portal_foreground_color = v;
        this._clearUnneededChanges();
    }

    public setTrackingPortalForegroundOpacity(v: number) {
        this._changes.tracking_portal_foreground_opacity = v;
        this._clearUnneededChanges();
    }

    public setPolicyRuleDisplay(v: POLICY_RULE_DISPLAY) {
        this._changes.policy_rule_display = v;
        this._clearUnneededChanges();
    }

    public setFooterBackgroundColor(v: string) {
        this._changes.footer_background_color = v;
        this._clearUnneededChanges();
    }

    public setFooterBackgroundOpacity(v: number) {
        this._changes.footer_background_opacity = v;
        this._clearUnneededChanges();
    }

    public setS2ELayoutType(v: S2E_LAYOUT_TYPE) {
        this._changes.s2e_layout_type = v;
        this._clearUnneededChanges();
    }

    public setSwitchToExchangeLayoutItemLink(index: number, link: string) {
        this._s2e_layout_data?.[index]?.setLink(link);
        this._s2e_layout_data = [ ...this._s2e_layout_data ];
    }

    public uploadSwitchToExchangeLayoutItemImage(index: number, type: string, base64_content: string) {
        this._s2e_layout_data?.[index]?.uploadImage(type, base64_content, () => {
            this._s2e_layout_data = [ ...this._s2e_layout_data ];
        });
    }

    public deleteSwitchToExchangeLayoutItemImage(index: number) {
        this._s2e_layout_data?.[index]?.deleteImage();
        this._s2e_layout_data = [ ...this._s2e_layout_data ];
    }

    public uploadReturnPortalCoverImage(type: string, base64_content: string) {
        this._changes._return_portal_new_cover_image = {
            base64_content,
            type
        };
        delete this._changes._return_portal_delete_cover_image;
        this._setReturnPortalCoverImageOriginalDimensions();
    }

    private _setReturnPortalCoverImageOriginalDimensions() {
        if (!this.returnPortalCoverImage) return;

        this._loadBackgroundImage(this.returnPortalCoverImage, (img: HTMLImageElement) => {
            this._return_portal_cover_image_dimensions = img.width + 'x' + img.height;
        })
    }

    public deleteReturnPortalCoverImage() {
        this._changes._return_portal_delete_cover_image = !!this._model.return_portal_cover_image;
        delete this._changes._return_portal_new_cover_image;
    }

    public hasChanges(): boolean {
        return !!Object.keys(this._changes).length || this._s2e_layout_data.some((item) => item.hasChanges());
    }

    public getUpdatedModel(): ICustomizationClassic {
        const updatedModel = {
            ...this._model,
            ...this._changes,
            s2e_layout_data: this._s2e_layout_data.map((item) => item.getUpdatedModel())
        };
        return updatedModel;
    }

    public discardChanges(): void {
        this._changes = {} as ICustomizationClassicChanges;
    }

    private _loadBackgroundImage(src: string, callback: (image: HTMLImageElement) => void) {
        const img = new Image();
        img.src = src;
        img.onerror = (err) => {
            console.error(err);
        };
        img.onload = () => {
            runInAction(() => callback(img));
        };
    }

    private _getNewBackgroundImageBase64Str(data?: INewBackgroundImageData): string | undefined {
        if (data) {
            return `data:${data.type};base64,${data.base64_content}`;
        }
        return undefined;
    }

    private _clearUnneededChanges() {
        Object.keys(this._changes).forEach(key => {
            const theKey = key as keyof ICustomizationClassic;
            if (this._changes[theKey] === this._model?.[theKey]) {
                delete this._changes[theKey];
            }
        });
    }

    public async load() {
        const customizations = await this.service.loadCustomisations();
        this.setModel(customizations);
    }

    public async save() {
        const updatedModel: WithOld<ICustomizationClassic> = this.getUpdatedModel();
        this._isSaving = true;

        try {
            updatedModel.__old = this._model;
            const responseCustomizations = await this.service.updateCustomizations(updatedModel);
            runInAction(() => {
                this.setModel(responseCustomizations);
                this._isSaving = false;
            });
        } catch (error) {
            runInAction(() => {
                this._isSaving = false;
            });
        }
    }
}

export default CustomizationClassicStore;

/** some fields receievd from an api has an incorrect type */
function ensureValidModelCopy(data: ICustomizationClassic): ICustomizationClassic {
    const toNum = (v: undefined | any) => v !== undefined ? Number(v) : v;
    const toBool = (v: undefined | any) => Boolean(v);

    return {
        ...data,
        min_embedded_offset_top: toNum(data.min_embedded_offset_top),
        sku_display: toBool(data.sku_display),
        display_vertical: toBool(data.display_vertical),
        show_actions_descriptions: toBool(data.show_actions_descriptions),
        order_tracking_show_eta: toBool(data.order_tracking_show_eta),
        top_margin: toNum(data.top_margin),
        bottom_margin: toNum(data.bottom_margin),
        container_margins: toNum(data.container_margins),
    };
}
